import { HttpErrorResponse } from '@angular/common/http';
import {
	Component,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { BillingErrorsService } from '@brand-lolly/app/billing/services/billing-errors.service';
import { GetCreditPacksService } from '@brand-lolly/app/billing/services/get-credit-packs.service';
import { GetPlansService } from '@brand-lolly/app/billing/services/get-plans.service';
import { SubscriptionsStateService } from '@brand-lolly/app/billing/services/subscriptions-state.service';
import {
	PlanEntitlementAlias,
	PlanInterval,
	ProductAlias
} from '@brand-lolly/app/billing/types/billing.enums';
import { presentCardDecline } from '@brand-lolly/app/billing/types/card-decline.types';
import { CreditPack } from '@brand-lolly/app/billing/types/mutations.types';
import { StorageKey } from '@brand-lolly/app/_models/local-storage-key';
import { UserJWTDetailsInterface } from '@brand-lolly/app/authentication-v2/models/auth.model';
import { FreezeSeatDialogService } from '@brand-lolly/app/user-management/team/dialogs/freeze-seat-dialog/freeze-seat-dialog.service';
import { InviteMemberDialogResult } from '@brand-lolly/app/user-management/team/dialogs/invite-member-dialog/invite-member-dialog.model';
import { InviteMemberDialogService } from '@brand-lolly/app/user-management/team/dialogs/invite-member-dialog/invite-member-dialog.service';
import { RemoveMemberDialogService } from '@brand-lolly/app/user-management/team/dialogs/remove-member-dialog/remove-member-dialog.service';
import { TeamService } from '@brand-lolly/app/user-management/team/services/team.service';
import {
	buildSeatRungs,
	flattenApplicationPlans,
	SeatRungOption
} from '@brand-lolly/app/user-management/team/types/seat-rungs.util';
import { CatalogPlan } from '@brand-lolly/app/billing/types/plan.types';
import {
	creditRequestMark,
	creditRequestMarkTooltip
} from '@brand-lolly/app/user-management/team/types/credit-request.util';
import {
	CreditRequest,
	CreditRequestStatus,
	isAccountOwner,
	SeatState,
	TeamAccount,
	TeamAction,
	teamActionAllowed,
	TeamActionId,
	TeamInvite,
	TeamMember,
	TeamPermissions,
	TeamRole,
	teamRoleLabel
} from '@brand-lolly/app/user-management/team/types/team.types';
import { parseTeamRefusal } from '@brand-lolly/app/user-management/team/types/team-refusal.util';
import { TeamRefusalCode } from '@brand-lolly/app/user-management/team/types/team.types';
import {
	LoButton,
	LoCard,
	LoDialog,
	LoDialogRef,
	LoDialogService,
	LoEmptyState,
	LoIcon,
	LoMenu,
	LoMenuDivider,
	LoMenuItem,
	LoMenuTrigger,
	LoNotice,
	LoRadioGroup,
	LoRadioOption,
	LoSkeleton,
	LoStatus,
	LoTable,
	LoTableCell,
	LoTableColumn,
	LoToastService,
	LoTooltip
} from 'lo-design-system';
import {
	exhaustMap,
	Subject,
	Subscription,
	take,
	takeUntil,
	timer
} from 'rxjs';
import { AnaChannelConnectService } from '@brand-lolly/app/_services/ana-channel-connect.service';

/** Where an owner goes to buy more seats. */
const PLANS_ROUTE = '/user-management/manage-plan';

/** What a row's menu can do. */
type RowAction = 'top_up' | 'freeze' | 'restore' | 'remove';

/** One entry of a row's menu. */
interface RowMenuItem {
	value: RowAction;
	title: string;
	disabled?: boolean;
	/** Shown on hover, e.g. why a greyed entry cannot run. */
	tooltip?: string;
	variant?: 'destructive';
	/** A divider is drawn above this entry. */
	separatorBefore?: boolean;
}

/** The plan and tier sentence, behind the seat line's information icon. */
const SEAT_TIER_NOTE =
	'Everyone you seat joins on your plan and tier; only their monthly credit allowance is theirs.';

/** How often a request mark's relative time is re-derived. */
const MARK_REFRESH_MS = 60_000;

/**
 * The wait for a top-up's credits to land on the member: a re-read of the
 * roster every 2 s, fifteen times — 30 s — stopping the moment the figure
 * moves. The charge is accepted at once and the credits arrive on Stripe's
 * webhook a moment later, and nothing tells this page when.
 */
const CREDIT_LANDING_POLL_MS = 2000;
const CREDIT_LANDING_POLLS = 15;

const OWNER_ROLE_TOOLTIP =
	'Holds the plan and pays for every seat. This seat cannot be frozen.';

/** Why Restore is greyed on a frozen row while the plan has no seat free. */
const RESTORE_NO_SEAT_REASON =
	'Every seat your plan admits is occupied. Freeze someone or move to a larger plan to restore this person.';

/** One member, prepared for display — no arithmetic left for the template. */
type MemberRow = {
	/** The MEMBERSHIP id, which is what the freeze and restore routes address. */
	id: string;
	name: string;
	email: string;
	roleLabel: string;
	/** Why the owner's row carries no seat action. Empty on every other row. */
	roleTooltip: string;
	seatLabel: string;
	seatTone: 'success' | 'neutral';
	allowance: string;
	/** "1,240", or an em dash where the server stated none. */
	credits: string;
	/** A top-up was accepted and its credits have not reached this member yet. */
	creditsPending: boolean;
	frozen: boolean;
	/** The owner's own seat, which cannot be frozen and is not an action row. */
	isOwner: boolean;
	freezeOffered: boolean;
	restoreOffered: boolean;
	restoreEnabled: boolean;
	/** A frozen seat the owner may take off the account for good. */
	removeOffered: boolean;
	busy: boolean;
	/**
	 * The member's open request for credits — set only for an owner who may
	 * answer it, so a member reading this page is never offered the answers.
	 */
	request: CreditRequest | null;
	/** "asked 5m", after the member's figure in the Credits cell. */
	requestMark: string;
	/** "Asked for more credits · 5 minutes ago", on the mark's tooltip. */
	requestMarkTooltip: string;
	/** The owner may buy this member credits — answering a request or not. */
	topUpOffered: boolean;
	/** The pack the owner has picked for this member. */
	topUpPackId: string | null;
	/** "Top up for $500" — the charge, named on the button that makes it. */
	topUpLabel: string;
	/** A top-up or a dismissal for this row is in flight. */
	topUpBusy: boolean;
	/** Why the owner's card declined the last top-up, in a sentence. */
	topUpDecline: string;
	/** Every action but the request's own Top up button. Empty = no menu. */
	menuItems: RowMenuItem[];
	/** "Actions for Ivan", so each row's trigger announces whose it is. */
	actionsLabel: string;
};

/** One outstanding invitation, prepared for display. */
type InviteRow = {
	id: string;
	email: string;
	allowance: string;
	expiry: string;
	busy: boolean;
};

/**
 * THE ACCOUNT'S TEAM — who is under it, what each seat costs the account, and
 * the two things an owner does to a seat.
 *
 * WHAT THIS REPLACED, because the shape of the old page explains several
 * decisions here. It read a sub-user list into a grid of cards and gated its
 * "Add member" card on `planDetails?.users?.total` — a field declared and never
 * assigned, so the control was unreachable for every account that ever loaded
 * the page. There was no navigation entry pointing at it either. The seat
 * arithmetic below therefore comes from the billing entitlement the billing
 * screen already reads, so the two screens state one number and neither is a
 * field waiting to be filled in.
 *
 * THE ORDER OF THE READS IS NOT SIGNIFICANT — and that has to be kept true, not
 * assumed. They are deliberately not joined: a failed permission matrix must
 * still leave the table on screen, and a failed invite list must not blank the
 * members. So every read that feeds a derived value re-derives the page when it
 * lands, the plan catalogue included: whether the owner may invite is decided
 * from its rungs, and a catalogue that only rebuilt the rows once left an owner
 * unable to invite anyone whenever the team read happened to land first.
 */
@Component({
	standalone: true,
	selector: 'app-team-page',
	templateUrl: './team-page.component.html',
	styleUrls: ['./team-page.component.scss'],
	imports: [
		LoButton,
		LoCard,
		LoDialog,
		LoEmptyState,
		LoIcon,
		LoMenu,
		LoMenuDivider,
		LoMenuItem,
		LoMenuTrigger,
		LoNotice,
		LoRadioGroup,
		LoSkeleton,
		LoStatus,
		LoTable,
		LoTableCell,
		LoTooltip
	]
})
export class TeamPageComponent implements OnInit, OnDestroy {
	/** The top-up dialog's body; opened with LoDialogService. */
	@ViewChild('topUpDialog', { static: true })
	private topUpDialog!: TemplateRef<unknown>;

	/** Starts TRUE: the members are fetched in ngOnInit, so the first paint of
	 *  an empty table would claim the account has nobody in it. */
	loading = true;
	/**
	 * Starts FALSE, unlike {@link loading}: the invitation list is fetched only
	 * once the role matrix says this caller may read it, so there is no request
	 * in flight on the first paint and a member never sees a placeholder for a
	 * section they will not be shown.
	 */
	invitesLoading = false;
	/** The members read failed — which is not the same as an account with none. */
	failed = false;
	/**
	 * The reader's OWN seat is frozen, so every route but the matrix refuses
	 * them. Its own state: "we could not read your team" would be a lie, and the
	 * one thing they need is who to ask.
	 */
	seatFrozen = false;

	rows: MemberRow[] = [];
	invites: InviteRow[] = [];
	/**
	 * Whether the Credits column is drawn — only when some row carries a figure,
	 * which is the owner's read. A member's read states none, and a column of
	 * dashes would say the team has no credits.
	 */
	showCredits = false;
	columns: LoTableColumn[] = teamColumns(false);
	readonly inviteColumns: LoTableColumn[] = [
		{ key: 'email', label: 'Email' },
		{ key: 'allowance', label: 'Allowance', width: '12rem' },
		{ key: 'expiry', label: 'Expires', width: '11rem' },
		{ key: 'actions', label: 'Actions', width: '7rem', hideLabel: true }
	];

	/**
	 * The seat line under the title, in three parts:
	 *
	 * - seatCountSentence, read out and not drawn: "2 of 5 seats occupied", or
	 *   "4 seats occupied" with no cap — the one sentence that says it all;
	 * - seatCountShort, drawn and not read out: "2 of 5", or "4 used";
	 * - seatCountTail, drawn and read: " · 3 free", " · no seat free",
	 *   " · your plan seats only you", " · no limit".
	 */
	seatCountSentence = '';
	seatCountShort = '';
	seatCountTail = '';
	/** Share of the seats occupied, 0 to 100 — null with no cap to measure against. */
	readonly seatTierNote = SEAT_TIER_NOTE;
	seatsFull = false;
	/**
	 * Every seat is taken and this reader may buy more, so the header's button
	 * is "Upgrade to invite" and leads to the plans page.
	 */
	upgradeToInvite = false;

	canInvite = false;
	/**
	 * Whether "See plans" is drawn beside a full account.
	 *
	 * A member may read this page and see that every seat is taken; what they
	 * may not do is go and buy more, so the button that leads to the plans page
	 * is theirs to see only if the matrix says they may manage billing.
	 *
	 * Starts true, like every other reading of this matrix in the app: the
	 * button is behind `seatsFull` anyway, which is computed in the same pass.
	 */
	canManageBilling = true;
	/** Why the invite control is greyed. Empty when it is not. */
	inviteBlockedReason = '';
	canRevokeInvite = false;

	inviteInFlight = false;

	/** The packs a top-up can buy, priced at the account's own tier. */
	packOptions: LoRadioOption[] = [];
	/** True once the pack catalogue has answered, even with nothing. */
	packsLoaded = false;
	/** The member whose top-up dialog is open, or null when none is. */
	topUpOpenFor: string | null = null;
	/**
	 * The open dialog's row, kept after the dialog starts closing so its body
	 * does not empty while it plays out. Every rebuild swaps in the fresh row.
	 */
	topUpRow: MemberRow | null = null;
	private topUpRef: LoDialogRef<unknown, void> | null = null;

	private permissions: TeamPermissions | null = null;
	private members: TeamMember[] = [];
	private rawInvites: TeamInvite[] = [];
	private rungs: SeatRungOption[] = [];
	/** The catalogue's application plans, kept so the rungs can be re-cut when the tier moves. */
	private applicationPlans: CatalogPlan[] | null = null;
	private rungLabelByPlanId = new Map<number, string>();
	private tierName = 'current';
	/** From the billing entitlement — the figure the billing screen shows. */
	private entitlementCap: number | null = null;
	private entitlementUsed: number | null = null;
	/** From GET /team, used only where the entitlement states nothing. */
	private teamSeatCap: number | null = null;
	private teamOccupied: number | null = null;
	private signedInUserId: number | null = null;
	private signedInEmail = '';
	private packs: CreditPack[] = [];
	private packsRequested = false;
	/**
	 * Per-member top-up state the rows are rebuilt from, keyed by membership id,
	 * so a Team re-read in the middle of a top-up keeps the owner's pick, the
	 * in-flight lock and the decline on the row they belong to.
	 */
	private topUpPackByMember = new Map<string, string>();
	private topUpBusyMembers = new Set<string>();
	private topUpDeclineByMember = new Map<string, string>();
	/**
	 * Members waiting on a top-up's credits, keyed by membership id, each with
	 * the figure they held when it was bought — the landing is that figure
	 * changing.
	 */
	private creditsLandingFrom = new Map<string, number | null>();
	private creditLandingPoll: Subscription | null = null;

	private readonly unsubscriber$ = new Subject<void>();

	constructor(
		private teamService: TeamService,
		private subscriptionsState: SubscriptionsStateService,
		private plansService: GetPlansService,
		private inviteDialog: InviteMemberDialogService,
		private freezeDialog: FreezeSeatDialogService,
		private toast: LoToastService,
		private dialogs: LoDialogService,
		private router: Router,
		private creditPacksService: GetCreditPacksService,
		private billingErrors: BillingErrorsService,
		private removeDialog: RemoveMemberDialogService,
		private anaConnect: AnaChannelConnectService
	) {}

	ngOnInit(): void {
		this.readSignedInIdentity();
		// Whoever re-reads the billing state — this page after a seat change,
		// the plans page after an upgrade — the cap, the tier and the rungs
		// that follow from them are re-derived here, so an account that has
		// just moved to a larger plan can invite without reloading the page.
		this.subscriptionsState.subscriptions$
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(() => this.applyEntitlement());
		this.loadEntitlement();
		this.loadRungs();
		this.loadTeam();
		// The invitations are loaded from inside this, once the matrix has said
		// whether this caller may read them.
		this.loadPermissions();
		// A request's "5 minutes ago" would otherwise stop ageing on a page left
		// open. A minute is the finest unit it states.
		timer(MARK_REFRESH_MS, MARK_REFRESH_MS)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(() => this.rebuildRows());
	}

	ngOnDestroy(): void {
		this.topUpRef?.close();
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	// ── Actions ──────────────────────────────────────────────────────────

	openInvite(opener?: HTMLElement): void {
		if (!this.canInvite || this.inviteInFlight) {
			return;
		}
		this.inviteInFlight = true;
		this.inviteDialog
			.open({ rungs: this.rungs, tierName: this.tierName }, opener)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(result => {
				this.inviteInFlight = false;
				if (!result.invited) {
					return;
				}
				this.toast.success(this.invitationSentNote(result));
				// The seat is spoken for the moment the invitation goes out, so
				// both the roster and the arithmetic behind it are re-read.
				this.refreshSeats();
				this.loadInvitesIfPermitted();
			});
	}

	freeze(row: MemberRow, opener?: HTMLElement | null): void {
		if (!row.freezeOffered || row.busy) {
			return;
		}
		this.freezeDialog
			.open({ memberLabel: row.name }, opener)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(result => {
				if (!result.confirmed) {
					return;
				}
				this.setRowBusy(row.id, true);
				this.teamService
					.freezeMember(row.id)
					.pipe(takeUntil(this.unsubscriber$))
					.subscribe({
						next: member => {
							this.applyMember(member);
							this.toast.success(`${row.name}'s seat is frozen.`);
							this.refreshSeats();
						},
						error: error => {
							this.setRowBusy(row.id, false);
							this.reportSeatRefusal(
								error,
								'That seat could not be frozen. Try again in a moment.'
							);
						}
					});
			});
	}

	restore(row: MemberRow): void {
		if (!row.restoreEnabled || row.busy) {
			return;
		}
		this.setRowBusy(row.id, true);
		this.teamService
			.restoreMember(row.id)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: member => {
					this.applyMember(member);
					this.toast.success(`${row.name} is back on the account.`);
					this.refreshSeats();
				},
				error: error => {
					this.setRowBusy(row.id, false);
					this.reportSeatRefusal(
						error,
						'That seat could not be restored. Try again in a moment.'
					);
				}
			});
	}

	/**
	 * Take a frozen member off the account for good, once the owner has read
	 * what that means. Only a frozen row offers it — the server refuses an
	 * active seat with seat_not_frozen — so the seat is already free and the
	 * figures move only because the roster no longer counts the person.
	 */
	remove(row: MemberRow, opener?: HTMLElement | null): void {
		if (!row.removeOffered || row.busy) {
			return;
		}
		this.removeDialog
			.open({ memberLabel: row.name }, opener)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(result => {
				if (!result.confirmed) {
					return;
				}
				this.setRowBusy(row.id, true);
				this.teamService
					.removeMember(row.id)
					.pipe(takeUntil(this.unsubscriber$))
					.subscribe({
						next: () => {
							// Off the page at once, rather than left on screen
							// until the re-read lands.
							this.members = this.members.filter(
								member => member.id !== row.id
							);
							this.setRowBusy(row.id, false);
							this.recompute();
							this.toast.success(
								`${row.name} has been removed from the account.`
							);
							this.refreshSeats();
						},
						error: error => {
							this.setRowBusy(row.id, false);
							this.reportSeatRefusal(
								error,
								'That person could not be removed. Try again in a moment.'
							);
						}
					});
			});
	}

	revokeInvite(invite: InviteRow): void {
		if (!this.canRevokeInvite || invite.busy) {
			return;
		}
		this.setInviteBusy(invite.id, true);
		this.teamService
			.revokeInvite(invite.id)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: () => {
					this.toast.success(
						`The invitation to ${invite.email} has been withdrawn.`
					);
					this.loadInvitesIfPermitted();
					this.refreshSeats();
				},
				error: error => {
					this.setInviteBusy(invite.id, false);
					this.reportSeatRefusal(
						error,
						'That invitation could not be withdrawn. Try again in a moment.'
					);
				}
			});
	}

	/**
	 * Open the top-up dialog for this row.
	 *
	 * ORDER: the opener is passed in because the menu item it may have been
	 * chosen from is gone by the time the dialog closes — focus goes back to the
	 * button that was pressed, or to the row's menu trigger.
	 */
	openTopUp(row: MemberRow, opener?: HTMLElement | null): void {
		if (!row.topUpOffered || this.topUpRef) {
			return;
		}
		this.topUpOpenFor = row.id;
		this.topUpRow = row;
		const ref = this.dialogs.openTemplate<void>(
			this.topUpDialog,
			'dialog',
			{
				opener: opener ?? undefined
			}
		);
		this.topUpRef = ref;
		ref.closed.pipe(takeUntil(this.unsubscriber$)).subscribe(() => {
			this.topUpRef = null;
			this.topUpOpenFor = null;
			this.topUpRow = null;
		});
	}

	/** One of the row menu's items. A disabled item never reaches here. */
	onRowAction(
		action: RowAction,
		row: MemberRow,
		actionsHost?: HTMLElement
	): void {
		// The menu's own trigger, so focus can return to it once a dialog opened
		// from the menu closes.
		const opener =
			actionsHost?.querySelector<HTMLElement>('[aria-haspopup="menu"]') ??
			null;
		switch (action) {
			case 'top_up':
				this.openTopUp(row, opener);
				return;
			case 'freeze':
				this.freeze(row, opener);
				return;
			case 'restore':
				this.restore(row);
				return;
			case 'remove':
				this.remove(row, opener);
				return;
		}
	}

	closeTopUp(): void {
		this.topUpOpenFor = null;
		this.topUpRef?.close();
	}

	/** The radio group's answer: the pack the owner picked for the open row. */
	onTopUpPackChange(packId: string | null): void {
		if (packId !== null && this.topUpRow) {
			this.selectTopUpPack(this.topUpRow, packId);
		}
	}

	selectTopUpPack(row: MemberRow, packId: string): void {
		if (!row.topUpOffered || row.topUpBusy) {
			return;
		}
		this.topUpPackByMember.set(row.id, packId);
		this.topUpDeclineByMember.delete(row.id);
		this.rebuildRows();
	}

	/**
	 * Buy the picked pack for this member, on the owner's card — answering their
	 * open request when they have one, and through the top-up route when not.
	 *
	 * The same three outcomes as the owner's own purchase in the Add credits
	 * dialog, answered the same way: a redirect goes to Stripe, a decline is
	 * worded by presentCardDecline and stays on the row with the request still
	 * open, anything else is the billing refusal it is.
	 */
	topUp(row: MemberRow): void {
		const request = row.request;
		const packId = row.topUpPackId;
		if (!row.topUpOffered || !packId || row.topUpBusy) {
			return;
		}
		const pack = this.packs.find(candidate => candidate.id === packId);
		this.setTopUpBusy(row.id, true);
		this.topUpDeclineByMember.delete(row.id);
		this.rebuildRows();
		// An open request is answered by buying for it; otherwise the owner is
		// topping up somebody who did not ask.
		const purchase$ = request
			? this.teamService.fulfilCreditRequest(request.id, {
					pack_id: packId
			  })
			: this.teamService.topUpMember(row.id, { pack_id: packId });
		purchase$.pipe(takeUntil(this.unsubscriber$)).subscribe({
			next: response => {
				if (response.kind === 'requires_redirect') {
					// Any request is already closed as fulfilled; the member
					// is credited once the owner authenticates.
					this.anaConnect.redirectExternally(response.url, 'billing');
					return;
				}
				this.finishTopUp(row.id, request?.id ?? null);
				this.toast.success(
					pack
						? `${formatCredits(
								pack.credits
						  )} credits are on their way to ${row.name}.`
						: `Credits are on their way to ${row.name}.`
				);
				// The member's balance moves once the payment webhook lands,
				// so the roster is re-read until it does rather than patched.
				this.awaitCreditsFor(row.id);
			},
			error: error => {
				this.setTopUpBusy(row.id, false);
				const declined = this.billingErrors.parseCardDecline(error);
				if (declined) {
					const presented = presentCardDecline(declined);
					this.topUpDeclineByMember.set(
						row.id,
						`${presented.title}. ${presented.cause}`
					);
					this.rebuildRows();
					return;
				}
				this.rebuildRows();
				if (this.reportClosedRequest(error)) {
					return;
				}
				// A frozen seat, the owner's own, a member already gone: the
				// row is stale, so say what the server said and re-read it.
				if (
					error instanceof HttpErrorResponse &&
					(error.status === 404 || error.status === 409)
				) {
					this.closeTopUp();
					this.toast.error(
						serverMessage(error) ??
							`Could not top up ${row.name}. Nothing has been charged.`
					);
					this.loadTeam(true);
					return;
				}
				this.billingErrors.handle(
					error,
					`Could not top up ${row.name}. Nothing has been charged.`
				);
			}
		});
	}

	dismissRequest(row: MemberRow): void {
		const request = row.request;
		if (!request || row.topUpBusy) {
			return;
		}
		this.setTopUpBusy(row.id, true);
		this.rebuildRows();
		this.teamService
			.dismissCreditRequest(request.id)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: () => {
					this.finishTopUp(row.id, request.id);
					this.toast.success(
						`${row.name}'s request is dismissed. Nothing was bought.`
					);
				},
				error: error => {
					this.setTopUpBusy(row.id, false);
					this.rebuildRows();
					if (this.reportClosedRequest(error)) {
						return;
					}
					this.toast.error(
						parseTeamRefusal(error)?.message ??
							'That request could not be dismissed. Try again in a moment.'
					);
				}
			});
	}

	retry(): void {
		this.loadTeam();
		this.loadPermissions();
	}

	goToPlans(): void {
		this.router.navigateByUrl(PLANS_ROUTE);
	}

	// ── Reads ────────────────────────────────────────────────────────────

	/**
	 * Read the roster.
	 *
	 * `background` is every re-read after the first: the table and the seat
	 * count stay on screen while it is in flight, instead of being swapped for
	 * skeletons under the owner who has just pressed a button in them.
	 */
	private loadTeam(background = false): void {
		if (!background) {
			this.loading = true;
		}
		this.teamService
			.getTeam()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: account => this.applyAccount(account),
				error: error => {
					this.loading = false;
					this.seatFrozen =
						parseTeamRefusal(error)?.code ===
						TeamRefusalCode.SEAT_FROZEN;
					this.failed = !this.seatFrozen;
					this.members = [];
					this.recompute();
				}
			});
	}

	private applyAccount(account: TeamAccount | null): void {
		this.loading = false;
		this.failed = false;
		this.seatFrozen = false;
		this.members = account?.members ?? [];
		this.teamSeatCap = account?.seat_cap ?? null;
		this.teamOccupied = account?.occupied_seats ?? null;
		this.settleLandedCredits();
		this.recompute();
	}

	/**
	 * Watch a member's figure until the credits the owner just bought for them
	 * land — the cell reads "Adding credits…" meanwhile, where a figure that
	 * stayed put would say the purchase did nothing.
	 *
	 * One poll serves every member being waited on; a second top-up restarts
	 * its 30 s window. It ends when nobody is waited on any more, or when the
	 * window runs out — a webhook slower than that is reflected on the next
	 * read of the page, not waited for.
	 */
	private awaitCreditsFor(memberId: string): void {
		const member = this.members.find(
			candidate => candidate.id === memberId
		);
		this.creditsLandingFrom.set(
			memberId,
			member?.credits_available ?? null
		);
		this.rebuildRows();
		this.creditLandingPoll?.unsubscribe();
		this.creditLandingPoll = timer(
			CREDIT_LANDING_POLL_MS,
			CREDIT_LANDING_POLL_MS
		)
			.pipe(
				take(CREDIT_LANDING_POLLS),
				exhaustMap(() => this.teamService.getTeam()),
				takeUntil(this.unsubscriber$)
			)
			.subscribe({
				next: account => this.applyAccount(account),
				// A failed read ends the wait rather than repeating a failure.
				error: () => this.stopAwaitingCredits(),
				complete: () => this.stopAwaitingCredits()
			});
	}

	/** Stop waiting on every member whose figure has moved since the top-up. */
	private settleLandedCredits(): void {
		if (!this.creditsLandingFrom.size) {
			return;
		}
		for (const [memberId, from] of this.creditsLandingFrom) {
			const member = this.members.find(
				candidate => candidate.id === memberId
			);
			if (!member || (member.credits_available ?? null) !== from) {
				this.creditsLandingFrom.delete(memberId);
			}
		}
		if (!this.creditsLandingFrom.size) {
			this.creditLandingPoll?.unsubscribe();
			this.creditLandingPoll = null;
		}
	}

	private stopAwaitingCredits(): void {
		this.creditLandingPoll?.unsubscribe();
		this.creditLandingPoll = null;
		if (this.creditsLandingFrom.size) {
			this.creditsLandingFrom.clear();
			this.rebuildRows();
		}
	}

	private loadPermissions(): void {
		this.teamService
			.getPermissions()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: permissions => {
					this.permissions = permissions;
					this.recompute();
					this.loadInvitesIfPermitted();
					this.loadPacksIfAnswering();
				},
				// A matrix we could not read leaves `permissions` null, which
				// reads as permissive — see `teamActionAllowed`. Greying an
				// owner out of their own account because one request failed is
				// the worse of the two failures.
				error: () => this.loadInvitesIfPermitted()
			});
	}

	/**
	 * The packs a top-up can buy — read once, only for an owner who may answer
	 * a request or top a member up. The owner's own tier prices them.
	 */
	private loadPacksIfAnswering(): void {
		if (
			this.packsRequested ||
			(!this.mayAnswerRequests() && !this.mayTopUp())
		) {
			return;
		}
		this.packsRequested = true;
		this.creditPacksService
			.get()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(packs => {
				this.packs = packs ?? [];
				this.packsLoaded = true;
				this.packOptions = this.packs.map(pack => ({
					label: `${formatCredits(pack.credits)} credits · ${
						pack.label
					}`,
					value: pack.id
				}));
				this.rebuildRows();
			});
	}

	/** The owner may buy credits for a member who has not asked. */
	private mayTopUp(): boolean {
		return (
			isAccountOwner(this.permissions) &&
			teamActionAllowed(this.permissions, TeamAction.TOP_UP)
		);
	}

	private mayAnswerRequests(): boolean {
		return (
			isAccountOwner(this.permissions) &&
			teamActionAllowed(
				this.permissions,
				TeamAction.CREDIT_REQUEST_ANSWER
			)
		);
	}

	/** The invitations, but only for a caller the matrix says may read them. */
	private loadInvitesIfPermitted(): void {
		if (teamActionAllowed(this.permissions, TeamAction.LIST_INVITES)) {
			this.loadInvites();
		}
	}

	private loadInvites(): void {
		this.invitesLoading = true;
		this.teamService
			.getInvites()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: invites => {
					this.invitesLoading = false;
					this.rawInvites = invites ?? [];
					this.rebuildInvites();
				},
				// A non-owner is refused this list by the role matrix, which is
				// the expected answer for them rather than a failure worth
				// reporting — the section simply does not appear.
				error: () => {
					this.invitesLoading = false;
					this.rawInvites = [];
					this.rebuildInvites();
				}
			});
	}

	private loadEntitlement(): void {
		this.subscriptionsState
			.ensureLoaded()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(() => this.applyEntitlement());
	}

	/**
	 * Re-read everything the seat arithmetic is made of — the roster AND the
	 * billing entitlement — after anything that moves it: a freeze, a restore,
	 * a removal, an invitation sent or withdrawn.
	 *
	 * Both halves, because the entitlement is the figure read first (see
	 * seatCap): re-reading the roster alone left the old figure in charge, so a
	 * page that had just frozen somebody still said every seat was taken and
	 * kept Invite and Restore greyed until a reload.
	 */
	private refreshSeats(): void {
		this.loadTeam(true);
		// TRAP: not tied to this page's lifetime on purpose — it refreshes the
		// app-wide store, and a read cancelled by navigating away would leave
		// every other screen on the old figure.
		this.subscriptionsState.load().subscribe({
			next: () => this.applyEntitlement(),
			error: () => this.applyEntitlement()
		});
	}

	private applyEntitlement(): void {
		this.readEntitlement();
		this.readTier();
		this.rebuildRungs();
		this.recompute();
	}

	/**
	 * The seat cap and its occupancy, off the SAME entitlement the billing
	 * screen renders.
	 *
	 * This is the whole of task 8.3: the two screens read one row, so they
	 * cannot state different numbers. `GET /team` restates the pair as well and
	 * is used only where this one says nothing — a plan with no `max_seats` row
	 * seeded on it.
	 */
	private readEntitlement(): void {
		// A billing read that failed leaves the store on whatever it held
		// before — after a seat change, exactly the figure this was re-read to
		// replace. GET /team restates the pair, so it answers instead.
		if (this.subscriptionsState.loadFailed) {
			this.entitlementCap = null;
			this.entitlementUsed = null;
			return;
		}
		const entitlement = this.subscriptionsState.getProductEntitlement(
			ProductAlias.APPLICATION,
			PlanEntitlementAlias.MAX_SEATS
		);
		this.entitlementCap = entitlement?.cap ?? null;
		this.entitlementUsed = entitlement?.used ?? null;
	}

	private readTier(): void {
		const tier = this.subscriptionsState.applicationTier;
		this.tierName = tier
			? tier.charAt(0).toUpperCase() + tier.slice(1)
			: 'current';
	}

	/**
	 * The allowances a new seat may be bought at.
	 *
	 * Read from the catalogue rather than from the account, because the account
	 * only says which rung the OWNER is on and a seat may be bought at any rung
	 * of the same tier. The tier is the account's; the rung is the choice.
	 */
	private loadRungs(): void {
		this.plansService
			.getAll()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(byProduct => {
				this.applicationPlans = flattenApplicationPlans(byProduct);
				this.rebuildRungs();
				// ORDER: this read and the team read land in either order, and
				// whether the owner may invite is decided from the rungs — so a
				// team read that lands first decided it against an empty ladder.
				// Rebuilding the rows alone left the owner unable to invite anyone.
				this.recompute();
			});
	}

	/**
	 * Cut the catalogue into the rungs of the account's CURRENT tier.
	 *
	 * Re-run whenever the billing state moves, not only when the catalogue
	 * lands: the rungs belong to the tier, so an upgrade that kept the old
	 * ladder would offer seats the server refuses as tier_mismatch.
	 */
	private rebuildRungs(): void {
		const plans = this.applicationPlans;
		if (plans === null) {
			return;
		}
		const current = this.subscriptionsState.subscriptions.find(
			subscription =>
				subscription.plan.alias.startsWith(ProductAlias.APPLICATION)
		);
		const tierLevel = current?.plan.tier_level ?? 0;
		const interval =
			(current?.plan.interval as PlanInterval) ?? PlanInterval.MONTH;
		this.rungs = buildSeatRungs(plans, tierLevel, interval);
		// Every application plan, not only this tier's rungs: a member
		// seated before a tier change still carries a rung from the
		// tier the account has left, and their row has to name it.
		this.rungLabelByPlanId = new Map(
			plans.map(plan => [plan.id, plan.title])
		);
		for (const rung of this.rungs) {
			this.rungLabelByPlanId.set(rung.planId, rung.label);
		}
	}

	// ── Derivation ───────────────────────────────────────────────────────

	private recompute(): void {
		this.recomputeOccupancy();
		this.rebuildRows();
		this.rebuildInvites();
	}

	/**
	 * The cap the plan states.
	 *
	 * THE BILLING ENTITLEMENT FIRST, and that ordering is the whole of task 8.3:
	 * the billing screen renders the same `max_seats` row, so reading it here is
	 * what makes the two screens unable to state different numbers. `GET /team`
	 * restates the pair and is the fallback for a brand whose billing read has
	 * not landed yet.
	 */
	private seatCap(): number | null {
		return this.entitlementCap ?? this.teamSeatCap;
	}

	/** How many seats are taken. A frozen seat is not one of them. */
	private occupiedSeats(): number {
		if (this.entitlementUsed !== null) {
			return this.entitlementUsed;
		}
		if (this.teamOccupied !== null) {
			return this.teamOccupied;
		}
		return this.members.filter(
			member => member.seat_state !== SeatState.FROZEN
		).length;
	}

	private seatsFree(): number | null {
		const cap = this.seatCap();
		return cap === null ? null : Math.max(cap - this.occupiedSeats(), 0);
	}

	private recomputeOccupancy(): void {
		const cap = this.seatCap();
		const occupied = this.occupiedSeats();
		const free = this.seatsFree();
		this.seatsFull = free !== null && free <= 0;

		// CONTRACT: the e2e specs read seatCountSentence off team-seat-count as
		// "N of M seat(s) occupied" — keep that wording.
		if (cap === null) {
			// No cap stated reads as no limit: an unlimited plan sends none.
			this.seatCountSentence = `${occupied} ${
				occupied === 1 ? 'seat' : 'seats'
			} occupied`;
			this.seatCountShort = `${occupied} used`;
			this.seatCountTail = ' · no limit';
		} else {
			this.seatCountSentence = `${occupied} of ${cap} ${
				cap === 1 ? 'seat' : 'seats'
			} occupied`;
			this.seatCountShort = `${occupied} of ${cap}`;
			if (!this.seatsFull) {
				this.seatCountTail = ` · ${free} free`;
			} else if (cap === 1) {
				this.seatCountTail = ' · your plan seats only you';
			} else {
				this.seatCountTail = ' · no seat free';
			}
		}

		this.canManageBilling = teamActionAllowed(
			this.permissions,
			TeamAction.BILLING_MANAGE
		);

		const ownerMayInvite =
			isAccountOwner(this.permissions) &&
			teamActionAllowed(this.permissions, TeamAction.INVITE);

		if (!ownerMayInvite) {
			this.canInvite = false;
			// The matrix states WHY each action is narrow, so the greyed
			// control and the 403 it is standing in for read the same sentence.
			this.inviteBlockedReason =
				this.actionReason(TeamAction.INVITE) ??
				'Only the account owner can invite people to this account.';
		} else if (this.seatsFull) {
			this.canInvite = false;
			this.inviteBlockedReason =
				'There is no free seat to put someone in.';
		} else if (!this.rungs.length) {
			this.canInvite = false;
			this.inviteBlockedReason =
				'We could not read the allowances on your plan.';
		} else {
			this.canInvite = true;
			this.inviteBlockedReason = '';
		}
		this.upgradeToInvite =
			ownerMayInvite && this.seatsFull && this.canManageBilling;
	}

	/** The matrix's own reason for narrowing an action, when it carries one. */
	private actionReason(actionId: TeamActionId): string | null {
		return (
			this.permissions?.actions?.find(action => action.id === actionId)
				?.reason || null
		);
	}

	private rebuildRows(): void {
		this.recomputeColumns();
		const ownerMayFreeze =
			isAccountOwner(this.permissions) &&
			teamActionAllowed(this.permissions, TeamAction.FREEZE);
		const ownerMayRestore =
			isAccountOwner(this.permissions) &&
			teamActionAllowed(this.permissions, TeamAction.RESTORE);
		const ownerMayRemove =
			isAccountOwner(this.permissions) &&
			teamActionAllowed(this.permissions, TeamAction.REMOVE);
		const seatAvailable = !this.seatsFull;
		const mayAnswer = this.mayAnswerRequests();
		const mayTopUp = this.mayTopUp();
		const defaultPackId = this.packs.length ? this.packs[0].id : null;
		const busy = new Set(
			this.rows.filter(row => row.busy).map(row => row.id)
		);

		// The owner first: it is the row a member is sent to by every refusal,
		// and GET /team states no order of its own.
		const ordered = [...this.members].sort(
			(a, b) =>
				Number(b.role === TeamRole.OWNER) -
				Number(a.role === TeamRole.OWNER)
		);
		this.rows = ordered.map(member => {
			const frozen = member.seat_state === SeatState.FROZEN;
			const isOwner = member.role === TeamRole.OWNER;
			const isYou = this.isSignedInMember(member);
			const name =
				(member.name && member.name.trim()) || member.email || '—';
			const request =
				mayAnswer &&
				member.credit_request?.status === CreditRequestStatus.OPEN
					? member.credit_request
					: null;
			const topUpOffered =
				!frozen && !isOwner && (request !== null || mayTopUp);
			const topUpPackId = topUpOffered
				? this.topUpPackByMember.get(member.id) ?? defaultPackId
				: null;
			const rowBusy = busy.has(member.id);
			const topUpBusy = this.topUpBusyMembers.has(member.id);
			const freezeOffered =
				!frozen && !isOwner && !isYou && ownerMayFreeze;
			const restoreOffered = frozen && !isOwner && ownerMayRestore;
			const restoreEnabled = restoreOffered && seatAvailable;
			const removeOffered = frozen && !isOwner && ownerMayRemove;
			const displayName = isYou ? `${name} (you)` : name;
			return {
				id: member.id,
				name: displayName,
				email: member.email,
				roleLabel: teamRoleLabel(member.role),
				roleTooltip: isOwner ? OWNER_ROLE_TOOLTIP : '',
				seatLabel: frozen ? 'Frozen' : 'Active',
				seatTone: (frozen ? 'neutral' : 'success') as
					| 'neutral'
					| 'success',
				allowance: this.allowanceLabel(member),
				credits: creditsLabel(member.credits_available),
				creditsPending: this.creditsLandingFrom.has(member.id),
				frozen,
				isOwner,
				// The owner's own seat is what pays for the rest, and freezing
				// the seat you are signed in on would lock you out of the screen
				// that could undo it.
				freezeOffered,
				restoreOffered,
				restoreEnabled,
				removeOffered,
				busy: rowBusy,
				request,
				requestMark: request ? creditRequestMark(request) : '',
				requestMarkTooltip: request
					? creditRequestMarkTooltip(request)
					: '',
				topUpOffered,
				topUpPackId,
				topUpLabel: this.topUpLabel(topUpPackId),
				topUpBusy,
				topUpDecline: this.topUpDeclineByMember.get(member.id) ?? '',
				menuItems: this.rowMenuItems({
					frozen,
					isOwner,
					topUpOffered,
					freezeOffered,
					restoreOffered,
					restoreEnabled,
					removeOffered,
					busy: rowBusy || topUpBusy
				}),
				actionsLabel: `Actions for ${displayName}`
			};
		});
		if (this.topUpOpenFor !== null) {
			this.topUpRow =
				this.rows.find(row => row.id === this.topUpOpenFor) ??
				this.topUpRow;
		}
	}

	/**
	 * A row's menu: every action but a request's own Top up button, which stays
	 * in the row where the request is.
	 *
	 * An action the reader may never take is left out; one they may take but
	 * not right now stays in the menu, greyed, with the reason on hover — so
	 * "why can I not restore this person" is answered where it is asked.
	 */
	private rowMenuItems(state: {
		frozen: boolean;
		isOwner: boolean;
		topUpOffered: boolean;
		freezeOffered: boolean;
		restoreOffered: boolean;
		restoreEnabled: boolean;
		removeOffered: boolean;
		busy: boolean;
	}): RowMenuItem[] {
		if (state.isOwner) {
			return [];
		}
		const items: RowMenuItem[] = [];
		if (state.frozen) {
			if (state.restoreOffered) {
				items.push({
					value: 'restore',
					title: 'Restore seat',
					disabled: !state.restoreEnabled || state.busy,
					tooltip: state.restoreEnabled ? '' : RESTORE_NO_SEAT_REASON
				});
			}
			if (state.removeOffered) {
				items.push({
					value: 'remove',
					title: 'Remove…',
					variant: 'destructive',
					disabled: state.busy
				});
			}
			return items;
		}
		if (state.topUpOffered) {
			items.push({
				value: 'top_up',
				title: 'Top up credits…',
				disabled: state.busy
			});
		}
		if (state.freezeOffered) {
			items.push({
				value: 'freeze',
				title: 'Freeze seat',
				disabled: state.busy,
				separatorBefore: items.length > 0
			});
		}
		return items;
	}

	private recomputeColumns(): void {
		this.showCredits = this.members.some(
			member =>
				member.credits_available !== null &&
				member.credits_available !== undefined
		);
		this.columns = teamColumns(this.showCredits);
	}

	private rebuildInvites(): void {
		// Listing and withdrawing are separate capabilities in the matrix, so
		// they are read separately here: a caller who may see the invitations
		// and not withdraw one gets the list with the control greyed, rather
		// than the list hidden.
		this.canRevokeInvite =
			isAccountOwner(this.permissions) &&
			teamActionAllowed(this.permissions, TeamAction.REVOKE_INVITE);
		const busy = new Set(
			this.invites.filter(row => row.busy).map(row => row.id)
		);
		this.invites = this.rawInvites.map(invite => ({
			id: invite.id,
			email: invite.email,
			allowance: this.rungLabelByPlanId.get(invite.rung_plan_id) ?? '—',
			expiry: formatExpiry(invite.expires_at),
			busy: busy.has(invite.id)
		}));
	}

	/**
	 * The seat's allowance, in the fullest form available.
	 *
	 * The catalogue's own rung line ("400 credits — $164/mo") is preferred over
	 * the plan title the roster carries, because it is the same string the
	 * invite dialog offered when the seat was bought. All three sources are null
	 * until seat subscriptions exist, which is why the em dash is a real answer
	 * here rather than a fallback for a broken read.
	 */
	private allowanceLabel(member: TeamMember): string {
		if (member.rung_plan_id !== null && member.rung_plan_id !== undefined) {
			const fromCatalogue = this.rungLabelByPlanId.get(
				member.rung_plan_id
			);
			if (fromCatalogue) {
				return fromCatalogue;
			}
		}
		return member.rung_title || '—';
	}

	private isSignedInMember(member: TeamMember): boolean {
		if (this.signedInUserId !== null && member.user_id !== null) {
			return member.user_id === this.signedInUserId;
		}
		return (
			!!this.signedInEmail &&
			member.email?.toLowerCase() === this.signedInEmail
		);
	}

	/**
	 * Who is reading the page, out of the token already in storage.
	 *
	 * Used for one thing — marking the reader's own row — so both halves are
	 * optional and a token that carries neither costs a "(you)" and nothing
	 * else.
	 */
	private readSignedInIdentity(): void {
		try {
			const raw = localStorage.getItem(StorageKey.decodedJwtIo);
			const decoded: UserJWTDetailsInterface = raw ? JSON.parse(raw) : {};
			const id = Number(decoded?.user_filed_id);
			this.signedInUserId = isNaN(id) ? null : id;
			this.signedInEmail = (decoded?.username ?? '').toLowerCase();
		} catch {
			this.signedInUserId = null;
			this.signedInEmail = '';
		}
	}

	/**
	 * Put a member the server just answered for straight onto the page.
	 *
	 * The freeze and restore responses ARE the member's new row, so the row is
	 * right at once instead of showing its old state until the reload lands —
	 * which is the window a second click would otherwise fall into.
	 */
	private applyMember(member: TeamMember): void {
		this.members = this.members.map(existing =>
			existing.id === member.id ? member : existing
		);
		// ORDER: cleared BEFORE the rebuild — rebuildRows carries every row's
		// busy flag forward, so one left set on success survives every later
		// reload and keeps the row's next action disabled until the page is
		// reloaded by hand.
		this.setRowBusy(member.id, false);
		this.recompute();
	}

	private setRowBusy(id: string, busy: boolean): void {
		this.rows = this.rows.map(row =>
			row.id === id ? { ...row, busy } : row
		);
	}

	private setTopUpBusy(memberId: string, busy: boolean): void {
		if (busy) {
			this.topUpBusyMembers.add(memberId);
		} else {
			this.topUpBusyMembers.delete(memberId);
		}
	}

	/** "Top up for $500", or the bare verb until a pack is picked. */
	private topUpLabel(packId: string | null): string {
		const pack = this.packs.find(candidate => candidate.id === packId);
		return pack ? `Top up for ${pack.label}` : 'Top up';
	}

	/**
	 * Clear a member's top-up once it is answered — and take an answered
	 * request off its row at once, rather than leaving the mark and its
	 * controls up until the re-read lands.
	 */
	private finishTopUp(memberId: string, requestId: string | null): void {
		if (this.topUpOpenFor === memberId) {
			this.closeTopUp();
		}
		this.setTopUpBusy(memberId, false);
		this.topUpPackByMember.delete(memberId);
		this.topUpDeclineByMember.delete(memberId);
		if (requestId !== null) {
			this.members = this.members.map(member =>
				member.credit_request?.id === requestId
					? { ...member, credit_request: null }
					: member
			);
		}
		this.rebuildRows();
	}

	/**
	 * A request somebody already answered — in another tab, or a moment ago.
	 * Says so and re-reads the roster, which no longer carries it.
	 */
	private reportClosedRequest(error: unknown): boolean {
		const refusal = parseTeamRefusal(error);
		if (refusal?.code !== TeamRefusalCode.REQUEST_CLOSED) {
			return false;
		}
		this.closeTopUp();
		this.toast.error(
			refusal.message ?? 'This request has already been answered.'
		);
		this.loadTeam(true);
		return true;
	}

	private setInviteBusy(id: string, busy: boolean): void {
		this.invites = this.invites.map(row =>
			row.id === id ? { ...row, busy } : row
		);
	}

	/**
	 * A refused seat operation, worded by its CODE.
	 *
	 * The greying is what should keep these from ever being seen; this is the
	 * second line, for the window between somebody else changing the account and
	 * this page re-reading it. Each branch also RE-READS whatever the refusal
	 * proves stale, so the surface corrects itself instead of only apologising.
	 */
	private reportSeatRefusal(error: unknown, fallback: string): void {
		const refusal = parseTeamRefusal(error);
		if (!refusal) {
			this.toast.error(fallback);
			return;
		}
		const message = refusal.message ?? fallback;
		switch (refusal.code) {
			case TeamRefusalCode.LIMIT_EXCEEDED:
				this.toast.error(message);
				this.refreshSeats();
				return;
			case TeamRefusalCode.ROLE_FORBIDDEN:
			case TeamRefusalCode.SEAT_FROZEN:
				this.toast.error(message);
				this.loadPermissions();
				return;
			case TeamRefusalCode.OWNER_SEAT_IMMUTABLE:
			case TeamRefusalCode.SEAT_NOT_FROZEN:
			case TeamRefusalCode.MEMBER_NOT_FOUND:
				this.toast.error(message);
				this.refreshSeats();
				return;
			default:
				this.toast.error(message);
		}
	}

	/**
	 * The acknowledgement after an invitation goes out.
	 *
	 * Names a figure only when the server priced the seating. `quote` is null on
	 * every response until the billing wave lands, and a sentence with a blank
	 * where an amount should be is worse than one that does not mention money.
	 */
	private invitationSentNote(result: InviteMemberDialogResult): string {
		const sent = `We have sent an invitation to ${result.email}.`;
		const quote = result.quote;
		if (!quote) {
			return sent;
		}
		const amount =
			typeof quote.amount === 'number'
				? quote.amount.toFixed(2)
				: quote.amount;
		return `${sent} ${amount} ${quote.currency} has been added for the rest of this period.`;
	}
}

/** The roster's columns; Credits is drawn only when the read states a figure. */
function teamColumns(showCredits: boolean): LoTableColumn[] {
	return [
		{ key: 'name', label: 'Member' },
		{ key: 'email', label: 'Email', width: '11rem' },
		{ key: 'role', label: 'Role', width: '9.5rem' },
		{ key: 'seat', label: 'Seat', width: '5.5rem' },
		{ key: 'allowance', label: 'Allowance', width: '8rem' },
		...(showCredits
			? [{ key: 'credits', label: 'Credits', width: '6.5rem' }]
			: []),
		{ key: 'actions', label: 'Actions', width: '4rem', hideLabel: true }
	];
}

function formatCredits(credits: number): string {
	return credits.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** "0", "1,240" — the column says what they are — or an em dash for no figure. */
function creditsLabel(credits: number | null | undefined): string {
	if (credits === null || credits === undefined) {
		return '—';
	}
	return formatCredits(credits);
}

/**
 * The sentence a refusal carried, whatever its code — a 409 on the top-up
 * route may carry one this page has no name for yet.
 */
function serverMessage(error: HttpErrorResponse): string | null {
	const body = error.error as { message?: unknown } | null;
	return typeof body?.message === 'string' && body.message
		? body.message
		: null;
}

/** "Expires 4 Oct 2026", or an empty string when the invite states no expiry. */
function formatExpiry(expiresAt: string | null | undefined): string {
	if (!expiresAt) {
		return '';
	}
	const when = new Date(expiresAt);
	if (isNaN(when.getTime())) {
		return '';
	}
	return `Expires ${when.toLocaleDateString('en-GB', {
		day: 'numeric',
		month: 'short',
		year: 'numeric'
	})}`;
}
